import { Badge, Button } from '@ohear/ui'
import { useNavigate } from '@tanstack/react-router'
/**
 * Single notification row. Used in both the dropdown panel and the full
 * notifications page — appearance is the same modulo the surrounding chrome.
 *
 * Click semantics:
 *   - If the row has a `link`, clicking the row navigates to it AND marks
 *     the row read in the same call.
 *   - Otherwise, clicking the row is a no-op; users mark via the explicit
 *     "Mark read" button.
 *
 * The "Mark read" button is hidden once the row is read so the action is not
 * misleading.
 */
import type { ReactNode } from 'react'
import type { Notification } from '../../lib/api/notifications'

export interface NotificationItemProps {
  notification: Notification
  /** Called with the notification id; should fire the mark-read mutation. */
  onMarkRead: (id: string) => void
  /** True while a mark-read mutation is in flight for THIS row. */
  isMarking?: boolean
  /** Tighter layout for the bell dropdown. */
  dense?: boolean
}

const TYPE_LABEL: Record<string, string> = {
  pickup_request: 'Pickup needed',
  take_approved: 'Take approved',
  take_submitted: 'Take submitted',
  comment_mention: 'Mention',
  milestone_paid: 'Payment',
  milestone_due: 'Payment due',
  batch_ready_for_review: 'Batch ready',
  system: 'System',
}

const TYPE_VARIANT: Record<string, 'gray' | 'blue' | 'green' | 'amber'> = {
  pickup_request: 'amber',
  take_approved: 'green',
  take_submitted: 'blue',
  comment_mention: 'blue',
  milestone_paid: 'green',
  milestone_due: 'amber',
  batch_ready_for_review: 'blue',
  system: 'gray',
}

export function NotificationItem({
  notification,
  onMarkRead,
  isMarking = false,
  dense = false,
}: NotificationItemProps) {
  const navigate = useNavigate()
  const unread = !notification.read_at
  const label = TYPE_LABEL[notification.type] ?? notification.type
  const variant = TYPE_VARIANT[notification.type] ?? 'gray'

  const open = () => {
    if (unread) onMarkRead(notification.id)
    if (notification.link) {
      // Treat link as in-app path; the navigate API understands string paths.
      void navigate({ to: notification.link })
    }
  }

  const wrapperBase = dense ? 'p-2' : 'p-3'
  const wrapperUnread = unread ? 'bg-[var(--accent)]/5' : ''
  const wrapper = `flex items-start gap-2 border-b border-[var(--border)] last:border-b-0 ${wrapperBase} ${wrapperUnread}`

  const Inner = (innerChildren: ReactNode) =>
    notification.link ? (
      <button
        type="button"
        onClick={open}
        className="flex-1 min-w-0 text-left bg-transparent border-0 p-0 m-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
        aria-label={notification.title}
      >
        {innerChildren}
      </button>
    ) : (
      <div className="flex-1 min-w-0">{innerChildren}</div>
    )

  return (
    <div role="listitem" className={wrapper}>
      {Inner(
        <>
          <div className="flex items-center gap-2 mb-0.5">
            <Badge variant={variant}>{label}</Badge>
            {unread ? (
              <span
                aria-label="Unread"
                className="inline-block w-2 h-2 rounded-full bg-[var(--accent,#1f2937)]"
              />
            ) : null}
            <span className="ml-auto text-[11px] text-[var(--text-3)]">
              {relativeTime(notification.created_at)}
            </span>
          </div>
          <div className="font-medium text-[13px] truncate">{notification.title}</div>
          {notification.body ? (
            <div className="text-[12px] text-[var(--text-2)] line-clamp-2">{notification.body}</div>
          ) : null}
        </>,
      )}
      {unread ? (
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation()
            onMarkRead(notification.id)
          }}
          loading={isMarking}
          aria-label={`Mark "${notification.title}" as read`}
        >
          Mark read
        </Button>
      ) : null}
    </div>
  )
}

function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime()
  const sec = Math.round(diffMs / 1000)
  if (sec < 60) return `${sec}s ago`
  const min = Math.round(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.round(min / 60)
  if (hr < 24) return `${hr}h ago`
  const day = Math.round(hr / 24)
  if (day < 7) return `${day}d ago`
  return new Date(iso).toLocaleDateString()
}
